import React from "react";
import dayjs from "dayjs";
import MuiSelect from "@mui/material/Select";
import InputBase from "@mui/material/InputBase";
import { IconType } from "react-file-icon";
import { alpha, styled } from "@mui/material/styles";
import { darkModeBackgroudInputs } from "../utils/constants";

export const formatComponentName = (name: string) => {
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
    .replace(/_/g, " ") // Replace underscores with spaces if any
    .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
};

export const formatNumber = (number: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

export const formatDate = (params: string) => {
  if (!params) return "";
  return dayjs(params).format("DD MMM YYYY hh:mm");
};

export const formatDateOnly = (params: string) => {
  if (!params) return "";
  return dayjs(params).format("DD MMMM YYYY");
};

export const capitalizeFirstLetter = (str: string | undefined) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const CardAvatar = (name: string) => {
  return (
    <span
      className="d-flex align-items-center justify-content-center fw-bold ms-2"
      style={{
        width: 50,
        height: 50,
        borderRadius: 8,
        background: "#eed8f7",
        color: "rgba(0,0,0,.5)",
      }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
};

export const StyledSelect = styled(MuiSelect)<{ darkmode: string }>(({ theme, darkmode }) => ({
  fontSize: 16,
  width: "100%",
  borderRadius: 4,
  padding: "1px 12px",
  border: "1px solid",
  position: "relative",
  color: darkmode === "true" ? "white" : "initial",
  borderColor: darkmode === "true" ? darkModeBackgroudInputs : "#E0E3E7",
  backgroundColor: darkmode === "true" ? darkModeBackgroudInputs : "#F3F6F9",
  transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
  fontFamily: [
    "-apple-system",
    "BlinkMacSystemFont",
    '"Segoe UI"',
    "Roboto",
    '"Helvetica Neue"',
    "Arial",
    "sans-serif",
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(","),
  "label + &": {
    marginTop: theme.spacing(3),
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "8px 12px",
  },
  "& .MuiSelect-icon": {
    fill: darkmode === "true" ? "white" : "black",
  },
  "&:focus": {
    boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
    borderColor: theme.palette.primary.main,
  },
}));

export const BootstrapInput = styled(InputBase)<{ darkmode: string }>(({ theme, darkmode }) => ({
  "label + &": {
    marginTop: theme.spacing(3),
  },
  "& .MuiSelect-icon": {
    fill: darkmode === "true" ? "white" : "black",
  },
  "& .MuiInputBase-input": {
    fontSize: 16,
    width: "100%",
    borderRadius: 4,
    padding: "8px 12px",
    position: "relative",
    border: "1px solid",
    color: darkmode === "true" ? "white" : "initial",
    backgroundColor: darkmode === "true" ? darkModeBackgroudInputs : "#F3F6F9",
    borderColor: darkmode === "true" ? darkModeBackgroudInputs : "#E0E3E7",
    transition: theme.transitions.create(["border-color", "background-color", "box-shadow"]),
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(","),
    "&:focus": {
      boxShadow: `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`,
      borderColor: theme.palette.primary.main,
    },
  },
}));

export const fileExtensionColors: { [key: string]: string } = {
  doc: "#1976D2",
  docx: "#1976D2",
  xls: "#89C0A2",
  xlsx: "#89C0A2",
  pdf: "#F15E54",
  json: "#EC9D2C",
  png: "#2493FF",
  jpeg: "#2493FF",
  jpg: "#2493FF",
  heic: "#9C27B0",
  heif: "#673AB7",
  file: "gray",
};
export const checkIconFile = (extension: string): IconType => {
  switch (extension) {
    case "pdf":
      return "acrobat";
    case "doc":
    case "docx":
      return "document";
    case "xls":
    case "xlsx":
      return "spreadsheet";
    case "png":
    case "jpeg":
    case "jpg":
      return "image";
    default:
      return "spreadsheet";
  }
};
export const getFileColor = (extension: string): string => {
  // Strip the leading dot if present and return the corresponding color
  const extensionKey = extension; // Remove the dot
  return fileExtensionColors[extensionKey] ?? "gray"; // Default to gray if not found
};

export const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
  const { target } = e;
  if (target.value.startsWith("0.")) {
  } else if (target.value === "0") target.value = "";
  else target.value = target.value.replace(/^0+(?!$)/, "");
};

export const handleExtension = (mimeType: string) => {
  let extension = "png";
  switch (mimeType) {
    case "image/jpeg":
      extension = "jpg";
      break;
    case "image/png":
      extension = "png";
      break;
    case "image/gif":
      extension = "gif";
      break;
    default:
      extension = "png";
  }
  return extension;
};

const currentYear = new Date().getFullYear();
export const getRecentYears = [
  { title: currentYear.toString(), id: currentYear, field: "" },
  { title: (currentYear - 1).toString(), id: currentYear - 1, field: "" },
  { title: (currentYear - 2).toString(), id: currentYear - 2, field: "" },
];
